import {useMemo} from 'react';

import {
  listOnlyVisibleRuns,
  numRunsHidden,
  numRunsVisible,
  panelSearchQuery,
  pinnedRunNamesPreview,
  reportRunSetInfo,
  runCountFiltered,
  runCountListed,
  runCountTotal,
  runSelectorGroupingKeys,
  runsFilter,
  sweepFacts,
  usePublishAgentPageContext,
  viewIdentity,
  visibleRunNamesPreview,
} from '../../../agentChat/pageContext';
import type {
  RunSelectorGroupingKey,
  SweepMetric,
  SweepPageContent,
  WorkspacePageContent,
} from '../../../agentChat/store/thread';
import {useMemoizedSelector} from '../../../state/hooks';
import {usePanelSearchInputContext} from '../../../state/panelbank/PanelSearchContextProvider';
import type * as CustomRunNamesTypes from '../../../state/views/customRunNames/types';
import * as ViewHooks from '../../../state/views/hooks';
import {useRunSetProject} from '../../../state/views/runSet/hooks';
import {type Ref as RunSetRef} from '../../../state/views/runSet/types';
import {getWorkspaceDisplayName} from '../../../state/workspaces/displayNames';
import {
  getDataMaybe,
  makeWorkspaceViewSelector,
} from '../../../state/workspaces/selectors';
import type {NamedWorkspaceId} from '../../../state/workspaces/types';
import * as Filter from '../../../util/filters';
import {getFiltersForQuery, type StoredFilter} from '../../../util/filterV2';
import {type Grouping} from '../../../util/queryTypes';
import {getCustomRunName, keysEqual} from '../../../util/runs';
import {GROUP_BY_ALL_KEY} from '../../../util/runTypes';
import * as SM from '../../../util/selectionmanager';
import {parseSweepState, type SweepYamlConfig} from '../../../util/sweeps';
import {usePinnedRuns, useRuns} from '../../RunSelector/RunSelectorRunsContext';
import {
  makeRunSetEnabledSelector,
  makeRunSetFiltersSelector,
  makeRunSetGroupingSelector,
  makeRunSetGroupSelectionsSelector,
  makeRunSetNameSelector,
  makeRunSetProjectSelector,
} from '../../RunSelector/runSetSelectors';
import {isCrossProjectRun} from '../../RunSelector/util';

type WorkspaceContextSliceProps = {
  workspaceID: string;
  viewType: NonNullable<WorkspacePageContent['viewType']>;
  nwId: NamedWorkspaceId | undefined;
};

/**
 * WBAgentWorkspaceContextSlice publishes the workspace view's identity (id,
 * name, type, scope) as one value. Mount once per active workspace.
 */
export const WBAgentWorkspaceContextSlice = ({
  workspaceID,
  viewType: viewTypeValue,
  nwId,
}: WorkspaceContextSliceProps) => {
  const viewState = useMemoizedSelector(makeWorkspaceViewSelector, workspaceID);
  const view = getDataMaybe(viewState)?.view;

  usePublishAgentPageContext(viewIdentity, {
    viewId: view?.id,
    viewName: view ? getWorkspaceDisplayName(view) : undefined,
    viewType: viewTypeValue,
    viewScope: nwTypeToScope(nwId),
  });

  return null;
};

type RunFilterContextSliceProps = {
  filters: StoredFilter;
};

/** WBAgentRunFilterContextSlice contributes the run filter expression. */
export const WBAgentRunFilterContextSlice = ({
  filters,
}: RunFilterContextSliceProps) => {
  usePublishAgentPageContext(
    runsFilter,
    serializeStoredFilter(filters) ?? null
  );
  return null;
};

type RunCountTotalContextSliceProps = {
  count: number | undefined;
};

/**
 * WBAgentRunCountTotalContextSlice contributes the view's total run count,
 * before any user filter.
 */
export const WBAgentRunCountTotalContextSlice = ({
  count,
}: RunCountTotalContextSliceProps) => {
  usePublishAgentPageContext(runCountTotal, count ?? null);
  return null;
};

type RunCountFilteredContextSliceProps = {
  count: number | undefined;
};

/**
 * WBAgentRunCountFilteredContextSlice contributes the run count after user
 * filter, view scope, and search. Subset of `runCountTotal`.
 */
export const WBAgentRunCountFilteredContextSlice = ({
  count,
}: RunCountFilteredContextSliceProps) => {
  usePublishAgentPageContext(runCountFiltered, count ?? null);
  return null;
};

type RunCountListedContextSliceProps = {
  count: number | undefined;
};

/**
 * WBAgentRunCountListedContextSlice contributes the run count listed in the
 * run selector.
 */
export const WBAgentRunCountListedContextSlice = ({
  count,
}: RunCountListedContextSliceProps) => {
  usePublishAgentPageContext(runCountListed, count ?? null);
  return null;
};

// Cap on how many visible run names the preview carries — enough for the agent
// to reference specific runs, short enough to keep the prompt compact.
const RUN_LIST_PREVIEW_LIMIT = 5;

type RunListPreviewContextSliceProps = {
  runSetRef: RunSetRef;
  customRunNamesRef?: CustomRunNamesTypes.Ref;
};

/**
 * WBAgentRunListPreviewContextSlice contributes the display names of the first
 * few visible (eye icon on) runs, so the agent can reference them without a
 * tool call. Reads the loaded run-selector rows and keeps those matching the
 * eyeball-visibility filter. Skipped for grouped views, whose top-level rows
 * are aggregates rather than individual runs. Pinned runs are published on a
 * separate channel: they stay visible regardless of the eyeball filter and are
 * individual runs even in grouped views.
 */
export const WBAgentRunListPreviewContextSlice = ({
  runSetRef,
  customRunNamesRef,
}: RunListPreviewContextSliceProps) => {
  const runs = useRuns();
  const pinnedRuns = usePinnedRuns();
  const groupSelections = useMemoizedSelector(
    makeRunSetGroupSelectionsSelector,
    runSetRef
  );
  // makeRunSetProjectSelector returns the raw config project, which is often
  // undefined on the main workspace run set. useRunSetProject resolves it,
  // falling back to the current project parsed from the URL.
  const rawRunSetProject = useMemoizedSelector(
    makeRunSetProjectSelector,
    runSetRef
  );
  const runSetProject = useRunSetProject(rawRunSetProject);
  const customRunNames = ViewHooks.usePartMaybe(customRunNamesRef);

  const preview = useMemo(() => {
    if (runs == null || runs.length === 0) {
      return null;
    }
    if (groupSelections.grouping.length > 0) {
      return null;
    }
    // toRunFilter encodes the eyeball-visibility state from the run set's
    // persistent selections — the same filter the selected-count query uses to
    // tally visible runs. (tempSelections is the bulk-select checkbox, a
    // different concern.)
    const visibleFilter = SM.toRunFilter({
      grouping: groupSelections.grouping,
      selections: groupSelections.selections,
    });
    const names: string[] = [];
    for (const run of runs) {
      if (names.length >= RUN_LIST_PREVIEW_LIMIT) {
        break;
      }
      if (Filter.match(visibleFilter, run)) {
        names.push(
          getCustomRunName(run.name, false, customRunNames) ??
            run.displayName ??
            run.name
        );
      }
    }
    return names.length > 0 ? names : null;
  }, [runs, groupSelections, customRunNames]);

  // No preview cap here: pinning is capped at MAX_PINNED_RUNS, so the full
  // list is already prompt-sized.
  const pinnedPreview = useMemo(() => {
    if (pinnedRuns == null || pinnedRuns.length === 0) {
      return null;
    }
    return pinnedRuns.map(run => {
      const name =
        getCustomRunName(run.name, false, customRunNames) ??
        run.displayName ??
        run.name;
      if (
        run.project != null &&
        isCrossProjectRun(run.project, {
          entityName: runSetProject.entityName,
          name: runSetProject.projectName,
        })
      ) {
        return `${name} (${run.project.entityName}/${run.project.name})`;
      }
      return name;
    });
  }, [pinnedRuns, customRunNames, runSetProject]);

  usePublishAgentPageContext(visibleRunNamesPreview, preview);
  usePublishAgentPageContext(pinnedRunNamesPreview, pinnedPreview);
  return null;
};

type RunVisibilityContextSliceProps = {
  visibleCount: number | undefined;
  hiddenCount: number | string | undefined;
  listOnlyVisibleRuns: boolean;
};

/**
 * WBAgentRunVisibilityContextSlice contributes the run-visibility signals: the
 * count of runs toggled visible (eye icon on), the count of runs listed but
 * hidden, and the "List only visible runs" toggle. The counts are computed in
 * the run selector (see RunsCountTag); on large projects the visible count is
 * dropped and the hidden count is a note string instead.
 */
export const WBAgentRunVisibilityContextSlice = ({
  visibleCount,
  hiddenCount,
  listOnlyVisibleRuns: listOnlyVisibleRunsValue,
}: RunVisibilityContextSliceProps) => {
  usePublishAgentPageContext(numRunsVisible, visibleCount ?? null);
  usePublishAgentPageContext(numRunsHidden, hiddenCount ?? null);
  usePublishAgentPageContext(listOnlyVisibleRuns, listOnlyVisibleRunsValue);
  return null;
};

/**
 * WBAgentPanelSearchContextSlice contributes the live panel-search query.
 * Reads from the input context (not view spec) because panel-bank save
 * lags typing by ~1s.
 */
export const WBAgentPanelSearchContextSlice = () => {
  const {searchQuery} = usePanelSearchInputContext();
  usePublishAgentPageContext(panelSearchQuery, searchQuery || null);
  return null;
};

type RunSelectorGroupingContextSliceProps = {
  runSetRef: RunSetRef;
};

/**
 * WBAgentRunSelectorGroupingContextSlice contributes the active run-selector
 * grouping dimensions. Emits undefined when no grouping is set.
 */
export const WBAgentRunSelectorGroupingContextSlice = ({
  runSetRef,
}: RunSelectorGroupingContextSliceProps) => {
  const groupingValue = useMemoizedSelector(
    makeRunSetGroupingSelector,
    runSetRef
  );
  usePublishAgentPageContext(
    runSelectorGroupingKeys,
    serializeGrouping(groupingValue) ?? null
  );
  return null;
};

type SweepContextSliceProps = {
  state: string | undefined;
  method: SweepYamlConfig['method'];
  metric: SweepYamlConfig['metric'];
  runCount: number | undefined;
  runCountExpected: number | undefined;
};

/**
 * WBAgentSweepContextSlice publishes the sweep's state and its config-derived
 * search method and optimization metric. Mount once per active sweep.
 */
export const WBAgentSweepContextSlice = ({
  state,
  method,
  metric,
  runCount,
  runCountExpected,
}: SweepContextSliceProps) => {
  usePublishAgentPageContext(sweepFacts, {
    state: normalizeSweepState(state),
    metric: toSweepMetric(metric),
    method,
    runCount,
    runCountExpected,
  });
  return null;
};

type ReportRunSetContextSliceProps = {
  runSetRef: RunSetRef;
};

/**
 * WBAgentReportRunSetContextSlice publishes a report run set's identity (name,
 * project, enabled) and its filter expression. Mount once per run set, inside
 * its AgentPageContextGroup.
 */
export const WBAgentReportRunSetContextSlice = ({
  runSetRef,
}: ReportRunSetContextSliceProps) => {
  const name = useMemoizedSelector(makeRunSetNameSelector, runSetRef);
  const project = useMemoizedSelector(makeRunSetProjectSelector, runSetRef);
  const enabled = useMemoizedSelector(makeRunSetEnabledSelector, runSetRef);
  const filters = useMemoizedSelector(makeRunSetFiltersSelector, runSetRef);

  usePublishAgentPageContext(reportRunSetInfo, {
    name: name || undefined,
    entityName: project?.entityName,
    projectName: project?.name,
    enabled,
  });
  usePublishAgentPageContext(
    runsFilter,
    serializeStoredFilter(filters) ?? null
  );

  return null;
};

function nwTypeToScope(
  nwId: NamedWorkspaceId | undefined
): WorkspacePageContent['viewScope'] {
  return nwId == null
    ? undefined
    : nwId.nwType === 'shared workspace'
      ? 'shared'
      : 'personal';
}

function serializeGrouping(
  groups: Grouping
): RunSelectorGroupingKey[] | undefined {
  // GROUP_BY_ALL_KEY is the "no real grouping" sentinel; drop it so the
  // field is absent when the user hasn't selected dimensions.
  const keys = groups.filter(k => !keysEqual(k, GROUP_BY_ALL_KEY));
  return keys.length > 0
    ? keys.map(k => ({section: k.section, name: k.name}))
    : undefined;
}

function serializeStoredFilter(filters: StoredFilter): string | undefined {
  const tree = getFiltersForQuery(filters);
  return Filter.countIndividual(tree) > 0 ? Filter.toString(tree) : undefined;
}

// normalizeSweepState maps the GraphQL state to the agent's lowercased union;
// an unrecognized or absent value resolves to the 'unknown' fallback.
function normalizeSweepState(
  state: string | undefined
): NonNullable<SweepPageContent['state']> {
  return parseSweepState(state).toLowerCase() as NonNullable<
    SweepPageContent['state']
  >;
}

// toSweepMetric drops a metric with no name, which the agent cannot use.
function toSweepMetric(
  metric: SweepYamlConfig['metric']
): SweepMetric | undefined {
  return metric?.name != null
    ? {name: metric.name, goal: metric.goal}
    : undefined;
}
